import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { useIntl } from 'react-intl';
import { Eye } from 'lucide-react';

import Button from 'src/componentes/button';
import {
  FormularioGlobalBuilder,
  FormularioGlobalCopiarColar,
} from 'src/componentes/formulario-global';
import { normalizeStoredImageSize } from 'src/componentes/formulario-global/FormularioGlobalResizableImage';
import {
  getQuestionOptionsOrDefault,
  normalizeFormularioGlobalQuestionType,
  normalizeQuestionCommentSettings,
  normalizeQuestionOptions,
  normalizeQuestionMatrixSettings,
  getQuestionMatrixValidationErrors,
  FORMULARIO_GLOBAL_QUESTION_TYPES,
} from 'src/componentes/formulario-global/FormularioGlobalQuestionTypes';
import http from 'src/services/httpService';
import useAuth from 'src/hooks/useAuth';
import errorHandler from 'src/utils/error-handler';

const TIPO_FORMULARIO_PESQUISA_CLIMA = {
  id: '1',
  codigo: 'PesquisaClima',
  label: 'labelPesquisaClima',
};

const createEmptyFormulario = () => ({
  id: 0,
  tipoFormulario: TIPO_FORMULARIO_PESQUISA_CLIMA,
  titulo: '',
  descricao: '',
  ativo: true,
  bloqueado: false,
  secoes: [],
  perguntas: [],
  usos: [],
});

const VALIDATION_LABELS = {
  SECTION_TITLE_REQUIRED: 'labelTituloSecaoObrigatorio',
  QUESTION_TITLE_REQUIRED: 'labelTituloPerguntaObrigatorio',
};
const FORMULARIO_GLOBAL_CLIENT_ID_FIELD = '_formularioGlobalClientId';

const ROOT_ENTRY_TYPE = {
  SECTION: 'section',
  QUESTION: 'question',
};

const stripFormularioGlobalClientId = (item) => {
  const nextItem = { ...item };
  delete nextItem[FORMULARIO_GLOBAL_CLIENT_ID_FIELD];
  return nextItem;
};

const hasValue = (value) => String(value || '').trim().length > 0;

const countPerguntas = (formulario) => {
  if (!formulario) return 0;

  const perguntasSecoes = (formulario.secoes || []).reduce(
    (total, secao) => total + (secao.perguntas || []).length,
    0
  );

  return perguntasSecoes + (formulario.perguntas || []).length;
};

const hasValidationErrors = (errors) =>
  Object.keys(errors?.sections || {}).length > 0 ||
  Object.keys(errors?.questions || {}).length > 0;

const getPerguntaValidationErrors = (pergunta) => ({
  ...(!hasValue(pergunta.titulo)
    ? { titulo: VALIDATION_LABELS.QUESTION_TITLE_REQUIRED }
    : {}),
  ...getQuestionMatrixValidationErrors(pergunta),
});

const getFormularioValidationErrors = (formulario) => {
  const errors = {
    sections: {},
    questions: {},
  };

  (formulario.secoes || []).forEach((secao, sectionIndex) => {
    const sectionErrors = {};

    if (!hasValue(secao.titulo)) {
      sectionErrors.titulo = VALIDATION_LABELS.SECTION_TITLE_REQUIRED;
    }

    (secao.perguntas || []).forEach((pergunta, questionIndex) => {
      const questionErrors = getPerguntaValidationErrors(pergunta);
      if (Object.keys(questionErrors).length > 0) {
        sectionErrors.questions = {
          ...(sectionErrors.questions || {}),
          [questionIndex]: questionErrors,
        };
      }
    });

    if (sectionErrors.titulo || sectionErrors.questions) {
      errors.sections[sectionIndex] = sectionErrors;
    }
  });

  (formulario.perguntas || []).forEach((pergunta, questionIndex) => {
    const questionErrors = getPerguntaValidationErrors(pergunta);
    if (Object.keys(questionErrors).length > 0) {
      errors.questions[questionIndex] = questionErrors;
    }
  });

  return errors;
};

const normalizePerguntas = (perguntas = [], intl) =>
  perguntas.map((pergunta, index) => {
    const imageSize = normalizeStoredImageSize(
      pergunta.imagemLargura,
      pergunta.imagemAltura
    );
    const tipo = normalizeFormularioGlobalQuestionType(
      pergunta.tipo || pergunta.tipoPergunta
    );
    const normalizedQuestion = normalizeQuestionMatrixSettings(
      normalizeQuestionCommentSettings({
        ...pergunta,
        tipo,
        permitirComentario: !!pergunta.permitirComentario,
        opcoes: getQuestionOptionsOrDefault(tipo, pergunta.opcoes || [], intl),
      })
    );

    return {
      ...normalizedQuestion,
      id: pergunta.id || 0,
      titulo: pergunta.titulo ?? '',
      tipo,
      obrigatoria: !!pergunta.obrigatoria,
      arquivoSistemaId: pergunta.arquivoSistemaId || null,
      imagem: pergunta.imagem || null,
      imagemLargura: imageSize.width,
      imagemAltura: imageSize.height,
      ordem: pergunta.ordem || index + 1,
    };
  });

const normalizeFormulario = (formulario, intl) => {
  const normalized = formulario || {};

  return {
    ...normalized,
    id: normalized.id || 0,
    tipoFormulario: normalized.tipoFormulario || TIPO_FORMULARIO_PESQUISA_CLIMA,
    titulo: normalized.titulo || '',
    descricao: normalized.descricao || '',
    ativo: normalized.ativo ?? true,
    bloqueado: !!normalized.bloqueado,
    usos: normalized.usos || [],
    secoes: (normalized.secoes || []).map((secao, index) => {
      const imageSize = normalizeStoredImageSize(
        secao.imagemLargura,
        secao.imagemAltura
      );

      return {
        ...secao,
        id: secao.id || 0,
        titulo: secao.titulo || '',
        descricao: secao.descricao || '',
        obrigatoria: !!secao.obrigatoria,
        arquivoSistemaId: secao.arquivoSistemaId || null,
        imagem: secao.imagem || null,
        imagemLargura: imageSize.width,
        imagemAltura: imageSize.height,
        perguntas: normalizePerguntas(secao.perguntas || [], intl),
        ordem: secao.ordem || index + 1,
      };
    }),
    perguntas: normalizePerguntas(normalized.perguntas || [], intl),
  };
};

const serializeOpcoes = (opcoes = []) =>
  normalizeQuestionOptions(opcoes).map((opcao, index) => ({
    ...opcao,
    texto: opcao.texto ?? '',
    valor: String(opcao.valor ?? index + 1),
    ordem: index + 1,
  }));

const serializePergunta = (pergunta, intl) => {
  const tipo = normalizeFormularioGlobalQuestionType(
    pergunta.tipo || pergunta.tipoPergunta
  );
  const serializedPergunta = normalizeQuestionMatrixSettings(
    normalizeQuestionCommentSettings({
      ...stripFormularioGlobalClientId(pergunta),
      tipo,
    })
  );

  return {
    ...serializedPergunta,
    titulo: pergunta.titulo ?? '',
    tipo,
    obrigatoria: !!pergunta.obrigatoria,
    ...(tipo === FORMULARIO_GLOBAL_QUESTION_TYPES.MATRIZ_SELECAO
      ? {
          linhas: serializedPergunta.linhas.map(({ id, texto, ordem }) => ({
            id,
            texto: String(texto ?? '').trim(),
            ordem,
          })),
          colunas: serializedPergunta.colunas.map(({ id, texto, ordem }) => ({
            id,
            texto: String(texto ?? '').trim(),
            ordem,
          })),
          permitirMultiplaSelecaoPorLinha:
            serializedPergunta.permitirMultiplaSelecaoPorLinha,
        }
      : {}),
    opcoes: serializeOpcoes(
      getQuestionOptionsOrDefault(tipo, serializedPergunta.opcoes || [], intl)
    ),
  };
};

const serializePerguntas = (perguntas = [], intl) =>
  perguntas.map((pergunta, index) => ({
    ...serializePergunta(pergunta, intl),
    ordem: index + 1,
  }));

const serializeFormulario = (formulario, targetEntidadeId, intl) => {
  const normalized = normalizeFormulario(formulario, intl);

  const orderedRootEntries = [
    ...(normalized.secoes || []).map((secao) => ({
      type: ROOT_ENTRY_TYPE.SECTION,
      ordem: secao.ordem || 0,
      secao,
    })),
    ...(normalized.perguntas || []).map((pergunta) => ({
      type: ROOT_ENTRY_TYPE.QUESTION,
      ordem: pergunta.ordem || 0,
      pergunta,
    })),
  ].sort((a, b) => a.ordem - b.ordem);

  const secoes = [];
  const perguntas = [];

  orderedRootEntries.forEach((rootEntry, index) => {
    const ordem = index + 1;

    if (rootEntry.type === ROOT_ENTRY_TYPE.SECTION) {
      secoes.push({
        ...stripFormularioGlobalClientId(rootEntry.secao),
        perguntas: serializePerguntas(rootEntry.secao.perguntas || [], intl),
        ordem,
      });

      return;
    }

    perguntas.push({
      ...serializePergunta(rootEntry.pergunta, intl),
      ordem,
    });
  });

  return {
    ...normalized,
    tipoFormulario: TIPO_FORMULARIO_PESQUISA_CLIMA,
    entidadeId: targetEntidadeId,
    secoes,
    perguntas,
  };
};

const FormularioPesquisaClimaBuilder = forwardRef(
  (
    {
      active,
      disabled,
      entidadeId,
      formularioId,
      onPreview,
      randomizarPerguntas = false,
    },
    ref
  ) => {
    const intl = useIntl();
    const { terms: resources } = useAuth();
    const [formulario, setFormulario] = useState(createEmptyFormulario());
    const [isLoading, setIsLoading] = useState(false);
    const [hasChanges, setHasChanges] = useState(false);
    const [validationErrors, setValidationErrors] = useState({});
    const hasLoadedRef = useRef(false);
    const originalRef = useRef(JSON.stringify(createEmptyFormulario()));
    const formularioRef = useRef(formulario);
    const formularioSerializedRef = useRef(JSON.stringify(formulario));
    const hasChangesRef = useRef(hasChanges);
    const validationErrorsRef = useRef({});
    const isEntidadePersisted = entidadeId > 0;
    const hasExistingFormulario = formularioId > 0;

    const updateFormulario = (nextFormulario, updateOriginal = false) => {
      const normalizedFormulario = normalizeFormulario(nextFormulario, intl);
      const serialized = JSON.stringify(normalizedFormulario);

      if (!updateOriginal && serialized === formularioSerializedRef.current) {
        return;
      }

      formularioRef.current = normalizedFormulario;
      formularioSerializedRef.current = serialized;
      setFormulario(normalizedFormulario);

      if (hasValidationErrors(validationErrorsRef.current)) {
        const nextValidationErrors =
          getFormularioValidationErrors(normalizedFormulario);
        validationErrorsRef.current = nextValidationErrors;
        setValidationErrors(nextValidationErrors);
      }

      if (updateOriginal) {
        originalRef.current = serialized;
        hasChangesRef.current = false;
        validationErrorsRef.current = {};
        setValidationErrors({});
        setHasChanges(false);
        return;
      }

      if (!hasChangesRef.current) {
        setHasChanges(true);
      }
      hasChangesRef.current = true;
    };

    const getFormulario = async () => {
      if (!hasExistingFormulario) {
        updateFormulario(createEmptyFormulario(), true);
        return;
      }

      setIsLoading(true);
      try {
        const response = await http.get('/FormularioGlobal/Edit', {
          params: {
            id: formularioId,
          },
        });

        updateFormulario(response.data?.model || response.data, true);
      } catch (err) {
        errorHandler(err);
        updateFormulario(createEmptyFormulario(), true);
      } finally {
        setIsLoading(false);
      }
    };

    useEffect(() => {
      if (active && !hasLoadedRef.current) {
        hasLoadedRef.current = true;
        getFormulario();
      }
    }, [active]);

    useEffect(() => {
      if (hasLoadedRef.current) {
        getFormulario();
      }
    }, [formularioId]);

    const validateFormularioGlobal = () => {
      const nextValidationErrors = getFormularioValidationErrors(
        formularioRef.current
      );

      validationErrorsRef.current = nextValidationErrors;
      setValidationErrors(nextValidationErrors);

      return !hasValidationErrors(nextValidationErrors);
    };

    useImperativeHandle(ref, () => ({
      getFormularioGlobal: () => ({
        ...formularioRef.current,
        randomizarPerguntas,
      }),
      getPerguntasCount: () => countPerguntas(formularioRef.current),
      validateFormularioGlobal,
      saveFormularioGlobal: async (entidadeIdOverride) => {
        const targetEntidadeId = entidadeIdOverride || entidadeId;
        const currentFormulario = formularioRef.current;

        if (
          !targetEntidadeId ||
          targetEntidadeId <= 0 ||
          !hasChangesRef.current
        ) {
          return null;
        }

        if (!validateFormularioGlobal()) {
          return null;
        }

        const payload = serializeFormulario(
          { ...currentFormulario, randomizarPerguntas },
          targetEntidadeId,
          intl
        );

        const response = await http.post('/FormularioGlobal/Save', payload);

        const responseModel = response.data?.model || response.data || {};

        const savedFormulario = normalizeFormulario(
          responseModel.secoes || responseModel.perguntas
            ? responseModel
            : {
                ...payload,
                ...responseModel,
              },
          intl
        );
        updateFormulario(savedFormulario, true);

        return savedFormulario;
      },
    }));

    const isFormularioDisabled =
      !isEntidadePersisted || disabled || formulario.bloqueado;

    const handleColarFormulario = (formularioColado) => {
      updateFormulario({
        ...createEmptyFormulario(),
        ...formularioColado,
        id: formularioRef.current?.id || 0,
      });
    };

    return (
      <FormularioGlobalBuilder
        blockedMessage={
          !isEntidadePersisted
            ? intl.formatMessage(
                { id: 'labelSalvePesquisaClimaAntesEditarFormulario' },
                { pesquisa: resources.pesquisa }
              )
            : formulario.bloqueado
            ? intl.formatMessage({
                id: 'labelFormularioGlobalBloqueado',
              })
            : null
        }
        disabled={isFormularioDisabled}
        exibirTipoFormularioIdentificacao={false}
        headerActions={
          <>
            {onPreview && (
              <Button
                color="link"
                leftIcon={Eye}
                iconSize={18}
                onClick={onPreview}
                style={{ color: '#6b7280', padding: 0, textDecoration: 'none' }}
              >
                {intl.formatMessage({ id: 'visualizar' })}
              </Button>
            )}
            <FormularioGlobalCopiarColar
              disabled={isFormularioDisabled || isLoading}
              formulario={formulario}
              onColar={handleColarFormulario}
            />
          </>
        }
        isLoading={isLoading}
        onChange={updateFormulario}
        tipoFormulario={TIPO_FORMULARIO_PESQUISA_CLIMA}
        validationErrors={validationErrors}
        value={formulario}
      />
    );
  }
);

export default FormularioPesquisaClimaBuilder;
